import * as React from 'react';
import {faBook, faMegaphone, faCopy as faCopySolid, faCheck} from '@fortawesome/pro-solid-svg-icons';
import {faCopy} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useEffect, useMemo, useState} from 'react';

import Button from '../../ui/Buttons/Button';
import {addItem} from '../compare/CompareFunctions';
import {AutoReordersService} from '../../../client/auto-reorders/auto-reorders.service';
import {AutoReordersWorkflow} from '../../../client/auto-reorders/auto-reorders.workflow';
import {GaItemListName} from '../../../client/ga/ga-ecommerce.functions';
import {gaLegacyCustomEvent, gaLegacyDataLayerPush} from '../../../client/ga/ga-legacy.functions';
import {ImpError} from '../../../client/imp-error/imp-error.class';
import {ItemToAdd} from '../../order-items/order-items.class';
import {Item} from '../item.class';
import {ListsWorkflow} from '../../../client/lists/lists.workflow';
import {OrderItemsWorkflow} from '../../../client/order-items/order-items.workflow';
import {OrdersService} from '../../../client/orders/orders.service';
import {Price} from '../../pricing/Price';
import {ProductFamilySelect} from './ProductFamilySelect';
import {Radio} from '../../ui/forms/Radio/Radio';
import {RestockNotificationForm} from './RestockNotificationForm';
import {ShipRestrictionsModal} from '../ship-restrictions/ShipRestrictionsModal';
import {Stepper} from '../../ui/forms/Stepper/Stepper';
import {StockStatus} from '../stock-status/StockStatus';
import {TemplateHelpers} from '../../tools/template-helpers.class';
import {useOrder} from '../../lib/hooks/use-order';
import {UsersService} from '../../../client/users/users.service';
import {UserStateService} from '../../../client/users/user-state.service';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';

interface ActionBlockProps {
    categoryPath: string;
    item: Item;
    onError: (errorMessage: string) => void;
    setShowAlternates: React.Dispatch<React.SetStateAction<boolean>>;
    shopOthersButton: string;
    user: User;
}

const componentName = `item-detail-component`;

export const ActionBlock = ({categoryPath, item, onError, setShowAlternates, shopOthersButton, user}: ActionBlockProps) => {
    const [actionLabel, setActionLabel] = useState(`Add To Order`);
    const [copyDoneState, setCopyDoneState] = useState(false);
    const [copyHoverState, setCopyHoverState] = useState(false);
    const [oneTimeDelivery, setOneTimeDelivery] = useState(true);
    const [showHoverState, setShowHoverState] = useState(false);
    const [showShipRestrictions, setShowShipRestrictions] = useState(false);
    const [unitsOrd, setUnitsOrd] = useState(1);
    const autoReordersService: AutoReordersService = useService(`autoReordersService`);
    const gaItemListName: GaItemListName = `IDP`;
    const listsWorkflow: ListsWorkflow = useService(`listsWorkflow`);
    const orderItemsWorkflow: OrderItemsWorkflow = useService(`orderItemsWorkflow`);
    const ordersService: OrdersService = useService(`ordersService`);
    const usersService: UsersService = useService(`usersService`);
    const userStateService: UserStateService = useService(`userStateService`);

    // Setup autoReordersWorkflow
    const autoReordersWorkflow = new AutoReordersWorkflow(autoReordersService, usersService, userStateService);

    // Setup useOrder hooks
    const {order} = useOrder({
        componentName,
    });

    useEffect(() => {
        if (ordersService.currentOrderNumber) {
            ordersService.getCurrentOrder().catch((loadError) => {
                onError(loadError.message);
            });
        }
        // On Initial page load, load order, if logged in
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Process queued actions
    useEffect(() => {
        if (userStateService.hasPendingAddToOrder(componentName)) {
            orderItemsWorkflow.addToOrderModal(userStateService.addToOrderItems, componentName, true);
        } else if (userStateService.hasPendingAddToList(componentName)) {
            listsWorkflow.addToList(
                userStateService.addToListAction,
                userStateService.addToListItems,
                componentName,
                componentName,
                userStateService.addToListItemDesc,
                userStateService.addToListItemImage,
            );
        } else if (userStateService.hasPendingEnrollAutoReorder(componentName)) {
            autoReordersWorkflow
                .enrollAutoReorder(componentName, userStateService.enrollAutoReorderItem)
                .then((enrollAutoReorderRes) => {
                    if (enrollAutoReorderRes) {
                        location.assign(`/manageautoreorders`);
                    }
                })
                .catch((enrollAutoReorderErr: ImpError) => {
                    onError(enrollAutoReorderErr.message);
                });
        }
        // Only run once per page load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * TBD
     */
    const canPurchase = useMemo(() => Item.userCanPurchase(item, user, order), [item, user, order]);

    /**
     * TBD
     */
    const doActualAdd = () => {
        const itemToAdd: ItemToAdd = {
            gaEcommerce: {item_list_name: gaItemListName},
            item: item.id,
            unitsOrdered: unitsOrd,
        };
        if (oneTimeDelivery) {
            gaLegacyCustomEvent({eventAction: `Add to Order Clicks`, eventCategory: `Ecommerce`, eventLabel: componentName});
            orderItemsWorkflow.addToOrderModal([itemToAdd], componentName, true);
        } else {
            autoReordersWorkflow
                .enrollAutoReorder(componentName, itemToAdd)
                .then((enrollAutoReorderRes) => {
                    if (enrollAutoReorderRes) {
                        location.assign(`/manageautoreorders`);
                    }
                })
                .catch((enrollAutoReorderErr: ImpError) => {
                    onError(enrollAutoReorderErr.message);
                });
        }
    };

    /**
     * TBD
     */
    const _addToOrderClicked = () => {
        if (!user.isLoggedIn() && item.excludeStArray?.length > 0) {
            setShowShipRestrictions(true);
        } else {
            doActualAdd();
        }
    };

    /**
     * Template
     */
    return (
        <div>
            {item.yourNumbers && <div className="tw-mb-2">My Item Number: {item.yourNumbers.join(` `)}</div>}
            <h2
                className="tw-mb-2"
                data-e2e="packageQty"
            >
                <span
                    className={`${showHoverState ? 'tw-bg-gray-100 ' : ''}tw-p-1 tw-pr-0 tw-mr-4 tw-text-base`}
                    onMouseEnter={() => {
                        setShowHoverState(true);
                    }}
                    onMouseLeave={() => {
                        setShowHoverState(false);
                    }}
                >
                    #{item.prettyid}
                    <span
                        className={`tw-ml-2 tw-text-base tw-p-1 tw-bg-gray-200 tw-cursor-pointer${showHoverState ? '' : ' tw-opacity-0'}`}
                        onMouseEnter={() => {
                            setCopyHoverState(true);
                        }}
                        onMouseLeave={() => {
                            setCopyHoverState(false);
                        }}
                        onClick={() => {
                            navigator.clipboard.writeText(item.prettyid);
                            setCopyDoneState(true);
                        }}
                    >
                        <FontAwesomeIcon icon={copyDoneState ? faCheck : copyHoverState ? faCopySolid : faCopy} />
                    </span>
                </span>
                <span
                    className="tw-text-base"
                    title="# of pieces per package sold"
                >
                    Pkg Qty: {item.pkgQty}
                </span>
            </h2>
            <div
                className="tw-mb-5"
                id="ItemPrice"
            >
                {!((item.inStock === false || item.itemtype === `D`) && item.isdiscontinue) && (
                    <div className="tw-font-bold tw-text-[calc(1.275rem+0.3vw)] tw-mb-2 tw-leading-[1.2] tw-m-0">
                        <Price
                            alias="item-detail-component"
                            item={item}
                            showCurrencyTooltip={!!((!user.isLoggedIn() || user.canViewPrice()) && item.price?.currDesc)}
                            user={user}
                        />
                    </div>
                )}
                {(!user.isLoggedIn() || user.canViewPrice()) && user.hasOneOfThesePriceBooks(['IL']) && (
                    <div>
                        Call for Personalized Pricing <br />
                        1-800-558-2808
                    </div>
                )}
            </div>
            {item.surcharge && <div>This item requires special shipping, additional charges may apply.</div>}
            {item.variants?.map((variant, index) => (
                <div
                    className="tw-my-3"
                    key={index}
                >
                    <ProductFamilySelect
                        variant={variant}
                        item={item}
                    />
                </div>
            ))}
            {item.pkgQtyVariants?.length > 0 && (
                <>
                    <h6>Package Quantity</h6>
                    <div
                        className="tw-mb-5"
                        id="packageQty"
                    >
                        {item.pkgQtyVariants.map((variant, index) => (
                            <Button
                                aria-label={`Quantity ${variant.qty}`}
                                className={`tw-mr-2 !tw-text-[16px] lg:!tw-text-[20px] !tw-rounded-none${
                                    variant.id === item.id ? ' tw-border-[3px] tw-border-black' : ''
                                }`}
                                diagonal={variant.outOfStock}
                                disabled={variant.outOfStock}
                                key={index}
                                onClick={() => {
                                    gaLegacyCustomEvent({
                                        eventAction: `qty_tiles`,
                                        eventCategory: `IDP`,
                                        eventLabel: `${variant.id}: ${variant.qty} | ${item.termSpec?.noun_of_item_spec || ''}`,
                                    });
                                    location.assign(`/item/${variant.id}`);
                                }}
                                size="lg"
                                variant="outline-secondary"
                            >
                                {variant.qty}
                            </Button>
                        ))}
                    </div>
                </>
            )}
            {item.purchases && (
                <div className="tw-p-3 tw-mb-5 tw-border tw-bg-gray-100">
                    Purchases:{' '}
                    <a
                        className="tw-font-bold"
                        href={`/order-history?q=${item.id}`}
                    >
                        {item.purchases}
                    </a>{' '}
                    | Last Purchased:{' '}
                    <a
                        className="tw-font-bold"
                        href={`/order-history/${item.lastPurchaseInvoice}`}
                    >
                        {TemplateHelpers.formatDate(item.lastPurchase)}
                    </a>
                </div>
            )}
            <div className="tw-p-6 tw-mb-2 tw-border">
                {!item.isdiscontinue && canPurchase && (
                    <div className="tw-mb-3">
                        <StockStatus
                            item={item}
                            unitsOrd={unitsOrd}
                            user={user}
                        />
                    </div>
                )}
                <form
                    className="print:tw-hidden"
                    onSubmit={(event) => {
                        event.preventDefault();
                        _addToOrderClicked();
                    }}
                >
                    <div className="tw-mb-3">
                        <Radio
                            alias="oneTimeDelivery"
                            defaultChecked={true}
                            disabled={!canPurchase}
                            label="One-time delivery"
                            name="deliveryType"
                            onChange={(event) => {
                                setOneTimeDelivery(event.target.checked);
                                if (event.target.checked) {
                                    setActionLabel(`Add To Order`);
                                }
                            }}
                        />
                        {item.canAutoReorder(user) && (
                            <Radio
                                alias="enrollInAutoReorder"
                                disabled={!canPurchase}
                                label="Enroll in auto-reorder"
                                name="deliveryType"
                                onChange={(event) => {
                                    setOneTimeDelivery(!event.target.checked);
                                    if (event.target.checked) {
                                        setActionLabel(`Enroll`);
                                    }
                                }}
                            />
                        )}
                    </div>
                    {item.qtyLimit && <div className="tw-mb-3 tw-font-bold">Max Order Qty: {Math.floor(item.qtyLimit / item.pkgQty)}</div>}
                    <div className="tw-flex tw-flex-wrap">
                        <div className="tw-w-full tw-mb-3 lg:tw-w-5/12">
                            <Stepper
                                className="[&_input]:!tw-h-[52px] tw-pr-0 lg:tw-pr-2 [&_input]:!tw-flex [&_input]:!tw-flex-grow [&_button]:!tw-h-[52px] [&_button]:!tw-w-[52px] [&_svg]:tw-text-2xl"
                                data-e2e="incrementQty"
                                disabled={item.discontinued}
                                maximum={item.qtyLimit > 0 ? Math.floor(item.qtyLimit / item.pkgQty) : 9999}
                                onUpdate={(qty) => {
                                    setUnitsOrd(qty);
                                }}
                                onUpdateAllowInvalid={false}
                                value={unitsOrd}
                            />
                        </div>
                        <div className="tw-w-full lg:tw-w-7/12 lg:tw-pl-3">
                            <Button
                                block
                                e2e="idp_AddToOrderBtn"
                                disabled={!canPurchase}
                                size="lg"
                                type="submit"
                                variant="primary"
                            >
                                {actionLabel}
                            </Button>
                            {shopOthersButton === `shopAlternateProducts` && (
                                <div className="tw-mb-3">
                                    <Button
                                        block
                                        className="tw-mt-3 tw-mb-2"
                                        e2e="itemAlternateButton"
                                        id="itemAlternateButton"
                                        onClick={() => {
                                            setShowAlternates(true);
                                        }}
                                        size="lg"
                                        type="button"
                                        variant="secondary"
                                    >
                                        Shop Alternates
                                    </Button>
                                </div>
                            )}

                            {shopOthersButton === `shopCategory` && (
                                <div className="tw-mb-3">
                                    <Button
                                        block
                                        className="tw-mt-3 tw-mb-2"
                                        e2e="shopCatagoryLink"
                                        id="shopCatagoryLink"
                                        onClick={() => location.assign(`/browse/${categoryPath}`)}
                                        size="lg"
                                        type="button"
                                        variant="secondary"
                                    >
                                        Shop Category
                                    </Button>
                                </div>
                            )}
                            <div className="tw-text-right">
                                <Button
                                    className="!tw-px-0"
                                    e2e="addToListLink"
                                    disabled={item.isdiscontinue}
                                    id="addToListLink"
                                    onClick={() => {
                                        listsWorkflow.addToList(
                                            `add`,
                                            [
                                                {
                                                    item: item.id,
                                                    quantity: 1,
                                                },
                                            ],
                                            componentName,
                                            componentName,
                                            item.name,
                                            item.imagePath,
                                        );
                                    }}
                                    size="lg"
                                    variant="link"
                                >
                                    Add to List
                                </Button>
                                {` | `}
                                <Button
                                    className="!tw-px-0"
                                    onClick={() => {
                                        addItem(item.id, item.imagePath);
                                    }}
                                    size="lg"
                                    variant="link"
                                >
                                    Compare
                                </Button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
            <RestockNotificationForm
                item={item}
                user={user}
            />
            <div
                className="tw-flex tw-flex-wrap"
                data-e2e="catalogLink"
            >
                {item.catalogPage && !user.isCatalogRestricted() && (
                    <div className="tw-w-full lg:tw-w-7/12 lg:tw-pt-1.5">
                        <FontAwesomeIcon
                            className="tw-mr-2"
                            icon={faBook}
                        />
                        Catalog:{' '}
                        <a
                            href={`/flyer/full-line-catalog/${item.catalogPage}/${item.prettyid}`}
                            rel="nofollow"
                            target="_blank"
                        >
                            {item.catalogPage}
                        </a>
                    </div>
                )}
                <div>
                    <Button
                        className="!tw-pl-0"
                        onClick={() => {
                            gaLegacyDataLayerPush(`injectQualtricsScriptIDP`);
                        }}
                        size="sm"
                        variant="link"
                    >
                        <FontAwesomeIcon
                            className="tw-mr-2"
                            icon={faMegaphone}
                        />
                        Help Us Improve
                    </Button>
                </div>
            </div>
            <ShipRestrictionsModal
                addToOrder={() => {
                    setShowShipRestrictions(false);
                    doActualAdd();
                }}
                gaItemListName="IDP"
                onHide={() => {
                    setShowShipRestrictions(false);
                }}
                orderableItem={item}
                show={showShipRestrictions}
                unitsOrdered={unitsOrd}
                useAddToOrderModal={false}
            />
        </div>
    );
};
